import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardBody, Col, Input, Label, Row, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { toast } from 'react-toastify'
import { Btn, H3 } from '../../../AbstractElements'
import { postJson, get } from '../../../hook/request'
import ManualInsertModal from './ManualInsertModal' 
import AddProductsModal from './AddProductsModal'


function normalizeBarcode(s: string) {
  return (s || '').trim()
}

function unwrapPayload(res: any) {
  return res?.data?.data ?? res?.data ?? res
}

type Scansionato = {
  ID: number
  codice: string
  bolla: string | null
  userID: number
  esito: number
  text: string | null
}


//modifica suoni:
// 
let audioCtx: AudioContext | null = null

function getAudioCtx() {
  if (!audioCtx) {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext
    audioCtx = new Ctx()
  }
  return audioCtx
}

async function beep({
  freq = 880,
  durationMs = 90,
  type = 'sine' as OscillatorType,
  gain = 1,
}: {
  freq?: number
  durationMs?: number
  type?: OscillatorType
  gain?: number
}) {
  const ctx = getAudioCtx()
  if (ctx.state === 'suspended') await ctx.resume()
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = type
  osc.frequency.value = freq
  g.gain.value = 0
  osc.connect(g)
  g.connect(ctx.destination)
  const now = ctx.currentTime
  const dur = durationMs / 1000
  g.gain.setValueAtTime(0, now)
  g.gain.linearRampToValueAtTime(gain, now + 0.01)
  g.gain.exponentialRampToValueAtTime(0.0001, now + dur)

  osc.start(now)
  osc.stop(now + dur)
}

async function playSuccessSound() {
  await beep({ freq: 1046, durationMs: 80, type: 'sine', gain: 1 })
}

async function playErrorSound() {
  await beep({ freq: 220, durationMs: 120, type: 'square', gain: 2 })
  await new Promise(r => setTimeout(r, 70))
  await beep({ freq: 196, durationMs: 140, type: 'square', gain: 2 })
}




export default function BarcodeScannerVerifica() {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const clearShortCodeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [codice, setCodice] = useState('')
  const [loading, setLoading] = useState(false)

  const [lastResult, setLastResult] = useState<string | null>(null)
  const [lastCodice, setLastCodice] = useState<string | null>(null)
  const sendingRef = useRef(false)

  const [alertColor, setAlertColor] = useState<'success' | 'warning' | 'danger'>('success')

  const [loadingList, setLoadingList] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [scansionati, setScansionati] = useState<Scansionato[]>([])
  const [numScansionati, setNumScansionati] = useState<number>(0)
  const [filtroModello, setFiltroModello] = useState('')
  const [filtroModelloDebounced, setFiltroModelloDebounced] = useState('')
  const [filtroOpen, setFiltroOpen] = useState(false)

  //modale per aggiungere il prodotto
  const [manualOpen, setManualOpen] = useState(false)
  const toggleManual = () => setManualOpen(v => !v)
  const [addProductOpen, setAddProductOpen] = useState(false)
  const [addProductCode, setAddProductCode] = useState('')
  const [addProductLoading, setAddProductLoading] = useState(false)
  const toggleAddProduct = () => setAddProductOpen(v => !v)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [duplicateScanId, setDuplicateScanId] = useState<number | null>(null)
  const [duplicateLoading, setDuplicateLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectedKo, setSelectedKo] = useState<Scansionato | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)


  const loadScansionati = async () => {
    setListError(null)
    setLoadingList(true)
    try {
      const query = filtroModelloDebounced.trim()
      const res = await get<any>('/scanner/scansionati', query ? { q: query } : undefined)
      const payload = unwrapPayload(res)

      // supporta varie forme: {status, data:[...]} oppure {data:[...]} oppure array diretto
      const status = String(payload?.status ?? '').toLowerCase()
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.list)
          ? payload.list
          : Array.isArray(payload)
            ? payload
            : []

      if (status === 'ok' || Array.isArray(list)) {
        setScansionati(list as Scansionato[])
        const rawNum = Number(payload?.num)
        setNumScansionati(Number.isFinite(rawNum) ? rawNum : list.length)
      } else {
        setScansionati([])
        setNumScansionati(0)
        setListError(String(payload?.message ?? 'Impossibile caricare la lista').trim())
      }
    } catch (e: any) {
      const payload = unwrapPayload(e?.response)
      setScansionati([])
      setNumScansionati(0)
      setListError(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    inputRef.current?.focus()
    loadScansionati()
  }, [filtroModelloDebounced])

  useEffect(() => {
    const t = setTimeout(() => {
      setFiltroModelloDebounced(filtroModello)
    }, 350)
    return () => clearTimeout(t)
  }, [filtroModello])

  const invia = async (raw?: string, qta: number = 1) => {
    const code = normalizeBarcode(raw ?? codice)
    if (!code) {
      toast.warn('Scansiona un barcode')
      return
    }

    try {
      setLoading(true)
      setLastResult(null)

      const res = await postJson('/scanner/verifica', {
        codice: code,
        qta: qta,       
      })

      const payload = (res as any)?.data ?? res
      const status = String(payload?.status ?? '').toLowerCase()
      const message =
        String(payload?.message ?? payload?.esito ?? payload?.result ?? '').trim() ||
        'Risposta non valida dal server'

      setLastResult(message)
      setLastCodice(code)

      if (status === 'ok') {
        setAlertColor('success')
        playSuccessSound().catch(() => {})
      } else if (status === 'okq') {
        const duplicateId = Number(
          payload?.scansioneID ??
          payload?.scansioneId ??
          payload?.data?.scansioneID ??
          payload?.data?.scansioneId
        )

        setAlertColor('warning')
        playSuccessSound().catch(() => {})
        setDuplicateScanId(Number.isFinite(duplicateId) ? duplicateId : null)
        setDuplicateOpen(true)
      } else {
        toast.error(message)
        setAlertColor('danger')
        playErrorSound().catch(() => {})
        if (code.length === 14) {
          setAddProductCode(code)
          setAddProductOpen(true)
        }
      }
      await loadScansionati()
    } catch (err: any) {
      toast.error(err?.message || 'Errore di rete')
      playErrorSound().catch(() => {})
      setCodice('')
    } finally {
      setLoading(false)
      setCodice('')
      sendingRef.current = false
      inputRef.current?.focus()
    }
  }

  const addProduct = async (codiceToAdd: string, qtaToAdd: number) => {
    const code = normalizeBarcode(codiceToAdd)
    const qty = Number(qtaToAdd)

    if (!code) return
    if (!Number.isFinite(qty) || qty < 1) return

    try {
      setAddProductLoading(true)
      const res = await postJson('/scanner/addPrd', {
        codice: code,
        qta: qty,
      })
      const payload = (res as any)?.data ?? res
      const status = String(payload?.status ?? '').toLowerCase()
      const message =
        String(payload?.message ?? payload?.esito ?? payload?.result ?? '').trim() ||
        'Risposta non valida dal server'

      if (status === 'ok') {
        toast.success(message)
        setLastCodice(code)
        setLastResult(message)
        setAlertColor('success')
        setAddProductOpen(false)
        await loadScansionati()
      } else {
        toast.error(message)
      }
    } catch (err: any) {
      toast.error(err?.message || 'Errore di rete')
    } finally {
      setAddProductLoading(false)
      inputRef.current?.focus()
    }
  }

  const openDeleteModal = (row: Scansionato) => {
    if (Number(row.esito) === 1) return
    setSelectedKo(row)
    setDeleteOpen(true)
  }

  const closeDeleteModal = () => {
    if (deleteLoading) return
    setDeleteOpen(false)
    setSelectedKo(null)
  }

  const deleteCronologiaRow = async () => {
    if (!selectedKo) return

    try {
      setDeleteLoading(true)
      const res = await postJson(`/scanner/delCrono?ID=${encodeURIComponent(String(selectedKo.ID))}`, {})
      const payload = unwrapPayload(res)
      const status = String(payload?.status ?? 'ok').toLowerCase()
      const message = String(payload?.message ?? 'Prodotto rimosso dalla cronologia').trim()

      if (status === 'ok') {
        toast.success(message || 'Prodotto rimosso dalla cronologia')
        setDeleteOpen(false)
        setSelectedKo(null)
        await loadScansionati()
        return
      }

      toast.error(message || 'Operazione non riuscita')
    } catch (err: any) {
      const payload = unwrapPayload(err?.response)
      toast.error(String(payload?.message ?? err?.message ?? 'Errore di rete').trim())
    } finally {
      setDeleteLoading(false)
    }
  }

  const closeDuplicateModal = () => {
    if (duplicateLoading) return
    setDuplicateOpen(false)
    setDuplicateScanId(null)
  }

  const deleteDuplicate = async () => {
    if (duplicateScanId == null) {
      toast.error('scansioneID non disponibile per il duplicato')
      return
    }

    try {
      setDuplicateLoading(true)
      const res = await postJson(`/scanner/delDuplicate?scansioneID=${encodeURIComponent(String(duplicateScanId))}`, {})
      const payload = unwrapPayload(res)
      const status = String(payload?.status ?? 'ok').toLowerCase()
      const message = String(payload?.message ?? 'Prodotto duplicato rimosso').trim()

      if (status === 'ok') {
        toast.success(message || 'Prodotto duplicato rimosso')
        setDuplicateOpen(false)
        setDuplicateScanId(null)
        await loadScansionati()
        return
      }

      toast.error(message || 'Operazione non riuscita')
    } catch (err: any) {
      const payload = unwrapPayload(err?.response)
      toast.error(String(payload?.message ?? err?.message ?? 'Errore di rete').trim())
    } finally {
      setDuplicateLoading(false)
    }
  }


  useEffect(() => {
    const value = normalizeBarcode(codice)
    if (clearShortCodeTimeoutRef.current) {
      clearTimeout(clearShortCodeTimeoutRef.current)
      clearShortCodeTimeoutRef.current = null
    }

    if (value.length === 14 && !loading && !sendingRef.current) {
      sendingRef.current = true
      invia(value)
      return
    }

    if (value.length > 0 && value.length < 14 && !loading) {
      clearShortCodeTimeoutRef.current = setTimeout(() => {
        setCodice(prev => (normalizeBarcode(prev).length < 14 ? '' : prev))
        toast.warning('lunghezza codice insufficiente');
      }, 450)
    }
  }, [codice, loading])

  useEffect(() => {
    return () => {
      if (clearShortCodeTimeoutRef.current) {
        clearTimeout(clearShortCodeTimeoutRef.current)
      }
    }
  }, [])

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!loading) invia(e.currentTarget.value)
    }
  }


  const rows = useMemo(() => scansionati.slice(0, 20), [scansionati])

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody className="p-0">

          <div className="p-3 pb-2 d-flex justify-content-between align-items-start gap-2">
            <div>
              <H3 className="mb-1">Scanner Barcode</H3>
              <small className="text-muted">Scansiona il codice.</small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block">Prodotti scansionati</small>
              <strong>{numScansionati}</strong>
            </div>
          </div>

          <div className="px-3 pb-2">
            <Btn
              style={{color:'black'}}
              color="light"
              type="button"
              className="btn-sm"
              onClick={() => setFiltroOpen(v => !v)}
            >
              {filtroOpen || filtroModello ? 'Nascondi Filtro' : 'Apri Filtro'}
            </Btn>
          </div>

          {(filtroOpen || filtroModello.length > 0) && (
            <div className="px-3 pb-2">
              <Label htmlFor="filtro-modello" className="mb-1">
                Filtro modello
              </Label>
              <Input
                id="filtro-modello"
                value={filtroModello}
                onChange={e => setFiltroModello(e.target.value)}
                placeholder="Cerca per nome modello..."
                autoComplete="off"
              />
            </div>
          )}

          <div
            style={{
              maxHeight: '60vh',           
              overflowY: 'auto',
              padding: '0 16px 12px',
            }}
          >
            {rows.map((it, idx) => (
              <div
                key={it.ID}
                className="py-2 border-bottom"
                onClick={() => openDeleteModal(it)}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginBottom: idx === 0 ? 32 : 0,
                  cursor: Number(it.esito) === 1 ? 'default' : 'pointer',
                }}
              >

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={it.codice}
                  >
                    {it.codice}
                  </div>

                  <div
                    className="text-muted"
                    style={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {it.text ?? '-'}
                  </div>
                </div>

                <span
                  className={`badge ${Number(it.esito) === 1 ? 'bg-success' : 'bg-danger'}`}
                  style={{
                    width: 42,              
                    textAlign: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  {Number(it.esito) === 1 ? 'OK' : 'KO'}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              borderTop: '1px solid rgba(0,0,0,.1)',
              padding: 16,
              zIndex: 2,
            }}
          >
     
            {lastCodice && (
              <Alert color={alertColor} className="mb-2">
                <div>
                  <strong>Codice:</strong> {lastCodice}
                  <br />
                  <strong>Esito:</strong> {lastResult ?? (loading ? 'Verifica in corso…' : '-')}
                </div>
              </Alert>
            )}

            <Label htmlFor="barcode" className="mb-1">
              Barcode
            </Label>

            <Input
              id="barcode"
              innerRef={inputRef as any}
              value={codice}
              onChange={e => setCodice(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Scansiona qui..."
              autoComplete="off"
              readOnly={loading}
              className={loading ? 'opacity-50' : ''}
            />

            <div className="d-flex gap-2 mt-2">
              <Btn
                color="primary"
                type="button"
                onClick={() => invia()}
                disabled={loading || !normalizeBarcode(codice)}
              >
                {loading ? <Spinner size="sm" /> : 'Verifica'}
              </Btn>

              <Btn
                color="secondary"
                outline
                type="button"
                onClick={() => setManualOpen(true)}
              >
                Inserisci manualmente
              </Btn>
            </div>
          </div>
        </CardBody>

      </Card>

      <ManualInsertModal
        isOpen={manualOpen}
        toggle={toggleManual}
        loading={loading}
        onConfirm={async (c, q) => {
          await invia(c, q)
          setManualOpen(false)
        }}
      />
      <AddProductsModal
        isOpen={addProductOpen}
        toggle={toggleAddProduct}
        initialCodice={addProductCode}
        loading={addProductLoading}
        onConfirm={addProduct}
      />
      <Modal isOpen={deleteOpen} toggle={closeDeleteModal} centered>
        <ModalHeader toggle={closeDeleteModal}>Rimuovi dalla cronologia</ModalHeader>
        <ModalBody>
          {selectedKo && (
            <>
              <div style={{ fontWeight: 700 }}>{selectedKo.codice}</div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {selectedKo.text ?? 'Esito KO'}
              </div>
              <Alert color="warning" className="mt-3 mb-0">
                Vuoi cancellare questo prodotto dalla cronologia?
              </Alert>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="light" style={{ color: 'black' }} onClick={closeDeleteModal} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button color="danger" onClick={deleteCronologiaRow} disabled={!selectedKo || deleteLoading}>
            {deleteLoading ? <Spinner size="sm" /> : 'Si'}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={duplicateOpen} toggle={closeDuplicateModal} centered>
        <ModalHeader toggle={closeDuplicateModal}>Conferma aggiunta</ModalHeader>
        <ModalBody>
          <Alert color="warning" className="mb-0">
            Hai superato la quantità prevista, sei sicuro di volerlo aggiungere?
          </Alert>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="danger" outline onClick={deleteDuplicate} disabled={duplicateLoading}>
            {duplicateLoading ? <Spinner size="sm" /> : 'No'}
          </Button>
          <Button color="primary" onClick={closeDuplicateModal} disabled={duplicateLoading}>
            Si
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  )
}
